import type {AnchorSide} from '@primer/behaviors'
import {ReposSelector} from '@github-ui/repos-selector'
import type {ButtonHTMLAttributes, ReactElement, RefObject} from 'react'

import type {Repository} from './repo'

interface RepoPickerProps {
  /**
   * Imperative loader the selector calls to fetch repositories for a query.
   * Fetching is intentionally the caller's responsibility so this package stays
   * purely presentational.
   */
  repositoryLoader: (query: string) => Promise<Repository[]>
  /** The currently selected repositories. Empty (or omitted) when none are selected. */
  selectedRepos?: Repository[]
  /** Reports the new selection (empty when cleared); persisting it is the caller's responsibility. */
  onUpdateRepos: (repos: Repository[]) => void
  /** The preferred side for the overlay to open relative to the anchor. */
  anchorSide?: AnchorSide
  /** When set, controls whether the picker overlay is open (controlled mode). */
  triggerOpen?: boolean
  /** Called when the overlay closes (Escape, click-away, or selection). */
  onClose?: () => void
  /** Existing visible button used as the controlled picker's overlay and focus-return anchor. */
  anchorRef?: RefObject<HTMLButtonElement | null>
  /** Optional trigger button overrides forwarded to ReposSelector. */
  additionalButtonProps?: ButtonHTMLAttributes<HTMLButtonElement>
  /** Optional scope hint rendered under the picker title (e.g. which owner's repos are shown). */
  subtitle?: string | ReactElement
  /**
   * When set, an owner scope is active: the picker shows a restriction shield beside
   * its title naming this owner (github-ui#31409). Forwarded to `ReposSelector`.
   */
  restrictionOwner?: string
}

/**
 * Slim, self-contained repository picker. Wraps the shared
 * `@github-ui/repos-selector` for a multi-select repo dropdown without pulling
 * in copilot-chat's chat service or manager. Data fetching is NOT done here —
 * the caller provides `repositoryLoader`. Selection is reported via
 * `onUpdateRepos`; persisting it is the caller's responsibility.
 */
export function RepoPicker({
  repositoryLoader,
  selectedRepos,
  onUpdateRepos,
  anchorSide,
  triggerOpen,
  onClose,
  anchorRef,
  additionalButtonProps,
  subtitle,
  restrictionOwner,
}: RepoPickerProps) {
  return (
    <ReposSelector<Repository>
      selectionVariant="multiple"
      selectAllOption={false}
      currentSelection={selectedRepos}
      repositoryLoader={repositoryLoader}
      onSelect={repos => onUpdateRepos(repos ?? [])}
      additionalButtonProps={{
        variant: 'invisible',
        type: 'button',
        ...additionalButtonProps,
      }}
      triggerOpen={triggerOpen}
      onClose={onClose}
      selectPanelRef={anchorRef}
      renderAnchor={anchorRef ? () => <></> : undefined}
      anchorSide={anchorSide}
      restrictionOwner={restrictionOwner}
      {...(subtitle ? {subtitle} : {})}
    />
  )
}
